import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';

import { LeaseDetailsForm } from '@/modules/application-tracker/components/lease-form';
import { useInviteLeaseDetailsDrawerStore } from '@/modules/application-tracker/stores';
import { checkIfPmi } from '@/utils/commonUtils';

import { inviteLeaseDetailsPreset } from './invite-lease-details.preset';
import type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';
import { InviteFormRuntimeContext } from './invite-lease-details.runtime-context';
import {
  useHasCustomScreeningStatusLabels,
  useInviteFormTracking,
  useInviteTracking,
} from './hooks';
import type { UseInviteLeaseDetailsControllerResult } from './hooks';
import type { InviteFlowMilestoneKey } from './types';

export type InviteLeaseDetailsSharedFormProps = {
  /** Hoisted by the drawer so legacy and shared share one controller instance. */
  controller: UseInviteLeaseDetailsControllerResult;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
};

// Config-native invite drawer (OTTERS-4671). Builds `inviteLeaseDetailsPreset`
// and hands it to the shared `<LeaseDetailsForm>`; the engine renders every
// section/field, derives the schema from config, and owns the RHF lifecycle.
// This component only assembles the preset deps and wires flow-start /
// validation-error analytics. The config depends only on the user's variant, so
// it is built once per drawer session.
export function InviteLeaseDetailsSharedForm({
  controller,
  onMilestoneReached,
}: InviteLeaseDetailsSharedFormProps) {
  const { user, errorMsg, isSmbInvite, useNewForm, customCoverageDisabled, onSubmit } = controller;
  const reportFormDirty = useInviteLeaseDetailsDrawerStore((state) => state.reportFormDirty);

  const isPmi = Boolean(user && checkIfPmi(user));

  const { trackInvite } = useInviteTracking();
  const hasCustomScreeningStatusLabels = useHasCustomScreeningStatusLabels(isSmbInvite);
  const { trackFlowStarted, trackValidationErrors } = useInviteFormTracking({
    onFlowStarted: () => onMilestoneReached?.('hadFlowStarted'),
  });

  // The controller's submit changes identity with its inputs; keep the config stable by calling
  // the latest one through a ref.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  const submitInvite = useCallback<InviteLeaseDetailsPresetDeps['onSubmit']>(
    (values, setError, attemptNumber) => onSubmitRef.current(values, setError, attemptNumber),
    [],
  );

  const handleValidationError = useCallback(
    (errors: FieldErrors) => {
      onMilestoneReached?.('hadSubmitAttempt');
      trackValidationErrors(errors, 'client');
    },
    [onMilestoneReached, trackValidationErrors],
  );

  const config = useMemo(
    () =>
      inviteLeaseDetailsPreset({
        isSmbInvite,
        isPmi,
        newCoveragesEnabled: useNewForm,
        customCoverageDisabled,
        trackInvite,
        onMilestoneReached,
        hasCustomScreeningStatusLabels,
        onSubmit: submitInvite,
      }),
    [
      isSmbInvite,
      isPmi,
      useNewForm,
      customCoverageDisabled,
      trackInvite,
      onMilestoneReached,
      hasCustomScreeningStatusLabels,
      submitInvite,
    ],
  );

  const runtime = useMemo(() => ({ errorMsg }), [errorMsg]);

  return (
    <InviteFormRuntimeContext.Provider value={runtime}>
      <div
        onFocusCapture={() => trackFlowStarted('field_focus')}
        onClickCapture={() => trackFlowStarted('click')}
        onKeyDownCapture={() => trackFlowStarted('typing')}
      >
        <LeaseDetailsForm
          config={config}
          mode="all"
          onValidationError={handleValidationError}
          // Drives the drawer's close confirmation.
          onDirtyChange={reportFormDirty}
        />
      </div>
    </InviteFormRuntimeContext.Provider>
  );
}
